// Shopify Page Tracker for Xano Integration
// This script tracks page visits and associates them with user emails

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    storageKey: 'userPageTracking',
    xanoEndpoint: 'https://xpsq-sdbm-bcmj.n7e.xano.io/api:FGUScUBu/page-tracking', // Replace with your actual Xano endpoint
    maxPages: 100, // Maximum pages to store
    maxAge: 30 * 24 * 60 * 60 * 1000, // 30 days in milliseconds
    debounceDelay: 1000 // Delay before saving page visit
  };

  // Main tracking object
  const PageTracker = {
    // Initialize the tracker
    init() {
      this.loadData();
      this.trackCurrentPage();
      this.setupEmailCapture();
      this.cleanOldData();
    },

    // Load existing data from localStorage
    loadData() {
      try {
        const data = localStorage.getItem(CONFIG.storageKey);
        this.data = data ? JSON.parse(data) : {
          email: null,
          sessionId: this.generateSessionId(),
          pages: []
        };
      } catch (error) {
        console.error('Error loading tracking data:', error);
        this.data = {
          email: null,
          sessionId: this.generateSessionId(),
          pages: []
        };
      }
    },

    // Save data to localStorage
    saveData() {
      try {
        localStorage.setItem(CONFIG.storageKey, JSON.stringify(this.data));
      } catch (error) {
        console.error('Error saving tracking data:', error);
        // Handle localStorage quota exceeded
        if (error.name === 'QuotaExceededError') {
          this.cleanOldData(true); // Force clean
          try {
            localStorage.setItem(CONFIG.storageKey, JSON.stringify(this.data));
          } catch (retryError) {
            console.error('Failed to save even after cleanup:', retryError);
          }
        }
      }
    },

    // Generate unique session ID
    generateSessionId() {
      return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    },

    // Track current page
    trackCurrentPage() {
      const pageData = {
        url: window.location.pathname,
        title: document.title,
        timestamp: new Date().toISOString(),
        referrer: document.referrer || null
      };

      // Check if this page was already tracked recently (within 5 seconds)
      const recentPage = this.data.pages.find(page => 
        page.url === pageData.url && 
        (new Date() - new Date(page.timestamp)) < 5000
      );

      if (!recentPage) {
        this.data.pages.push(pageData);
        
        // Limit the number of stored pages
        if (this.data.pages.length > CONFIG.maxPages) {
          this.data.pages = this.data.pages.slice(-CONFIG.maxPages);
        }

        // Save to localStorage
        this.saveData();

        // If we have an email, send to Xano immediately
        if (this.data.email) {
          this.debounce(() => this.sendToXano([pageData]), CONFIG.debounceDelay);
        }
      }
    },

    // Clean old data (older than maxAge)
    cleanOldData(force = false) {
      const now = new Date();
      const oldLength = this.data.pages.length;
      
      this.data.pages = this.data.pages.filter(page => {
        const pageDate = new Date(page.timestamp);
        return (now - pageDate) < CONFIG.maxAge;
      });

      // If forced or if we removed pages, save
      if (force || oldLength !== this.data.pages.length) {
        this.saveData();
      }
    },

    // Setup email capture listeners
    setupEmailCapture() {
      // Universal form email capture
      this.captureAnyFormEmail();
      
      // Customer login/registration capture
      this.captureCustomerEmail();
      
      // Checkout email capture
      this.captureCheckoutEmail();
    },

    // Capture email from ANY form submission
    captureAnyFormEmail() {
      document.addEventListener('submit', (e) => {
        const form = e.target;
        
        // Find all email inputs in the form
        const emailInputs = form.querySelectorAll('input[type="email"]');
        
        // Also check for common email field names (in case type="text" is used)
        const emailByName = form.querySelectorAll('input[name*="email" i], input[name*="e-mail" i]');
        
        // Combine and check all potential email fields
        const allEmailInputs = new Set([...emailInputs, ...emailByName]);
        
        for (const input of allEmailInputs) {
          if (input.value && this.isValidEmail(input.value)) {
            console.log('Email captured from form:', input.value);
            this.setEmail(input.value);
            break; // Use the first valid email found
          }
        }
      });
    },

    // Validate email format
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    // Capture customer email from login/registration
    captureCustomerEmail() {
      // Check if customer is logged in (Shopify provides this)
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && 
          window.ShopifyAnalytics.meta.page && window.ShopifyAnalytics.meta.page.customerId) {
        
        // Try to get email from customer object if available
        this.fetchCustomerEmail();
      }

      // Also listen for login/registration forms
      document.addEventListener('submit', (e) => {
        const form = e.target;
        
        if (form.action && (form.action.includes('/account/login') || 
            form.action.includes('/account/register'))) {
          
          const emailInput = form.querySelector('input[type="email"]');
          if (emailInput && emailInput.value) {
            this.setEmail(emailInput.value);
          }
        }
      });
    },

    // Capture email from checkout
    captureCheckoutEmail() {
      // Listen for Shopify checkout events if available
      document.addEventListener('page:load', () => {
        if (window.Shopify && window.Shopify.checkout) {
          const email = window.Shopify.checkout.email;
          if (email) {
            this.setEmail(email);
          }
        }
      });

      // Also check for email in checkout form
      const checkCheckoutEmail = () => {
        const emailInput = document.querySelector('#checkout_email, input[name="checkout[email]"]');
        if (emailInput && emailInput.value) {
          this.setEmail(emailInput.value);
        }
      };

      // Check periodically on checkout page
      if (window.location.pathname.includes('/checkout')) {
        setInterval(checkCheckoutEmail, 2000);
      }
    },

    // Fetch customer email via AJAX (if customer is logged in)
    fetchCustomerEmail() {
      fetch('/account.json', {
        credentials: 'same-origin'
      })
      .then(response => response.json())
      .then(data => {
        if (data.customer && data.customer.email) {
          this.setEmail(data.customer.email);
        }
      })
      .catch(error => {
        console.log('Could not fetch customer data:', error);
      });
    },

    // Set email and sync data
    setEmail(email) {
      if (email && email !== this.data.email) {
        this.data.email = email;
        this.saveData();
        
        // Send all tracked pages to Xano
        if (this.data.pages.length > 0) {
          this.sendToXano(this.data.pages);
        }
      }
    },

    // Send data to Xano
    sendToXano(pages = null) {
      if (!this.data.email || !CONFIG.xanoEndpoint) {
        return;
      }

      const dataToSend = {
        email: this.data.email,
        pages: pages || this.data.pages,
        session_id: this.data.sessionId
      };

      fetch(CONFIG.xanoEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend)
      })
      .then(response => {
        if (response.ok) {
          console.log('Page tracking data sent successfully');
          // Optionally clear sent pages
          if (!pages) {
            this.data.pages = [];
            this.saveData();
          }
        } else {
          console.error('Failed to send tracking data:', response.statusText);
        }
      })
      .catch(error => {
        console.error('Error sending tracking data:', error);
      });
    },

    // Debounce function to prevent too many API calls
    debounce(func, wait) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(func, wait);
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => PageTracker.init());
  } else {
    PageTracker.init();
  }

  // Make PageTracker available globally for debugging
  window.PageTracker = PageTracker;

})();